import * as Cesium from 'cesium';
import { getPlacemarkIconCanvas, getPlacemarkFarDotCanvas, getCommentBadgeCanvas, getCommentAvatarCanvas, placemarkLabelOffsetY, } from './placemarkIcons/placemarkIconRenderer';
// Camera altitude (m) above which a placemark renders as a plain dot
// instead of the full badge. The dot fights clutter when you have many
// placemarks visible from far away. NearFarScalar then shrinks the dot
// proportionally; together they give a smooth shrink → swap → shrink.
export const PLACEMARK_BADGE_TO_DOT_ALTITUDE = 75000;
export const PLACEMARK_SCALE_BY_DISTANCE = new Cesium.NearFarScalar(500, 1.0, 50000, 0.6);
export const ENTITY_STYLES = {
    point: { pixelSize: 14, outlineWidth: 2 },
    polygonVertex: { pixelSize: 12, outlineWidth: 2 },
    line: { width: 5 },
    highlight: { width: 12, alpha: 0.4 },
    measurementEndOutline: 3,
    label: { font: '14px sans-serif', outlineWidth: 2 },
    segmentLabel: { font: '12px sans-serif', outlineWidth: 2 }
};
const VERTEX_SCALE_BY_DISTANCE = new Cesium.NearFarScalar(500, 1.0, 5e4, 0.15);
const LINE_WIDTH_NEAR_ALT = 500;
const LINE_WIDTH_FAR_ALT = 5e4;
const LINE_WIDTH_FAR_SCALE = 0.3;
export const makePolylineWidthProperty = (baseWidth, viewer) => new Cesium.CallbackProperty(() => {
    var _a, _b, _c;
    if (!viewer || ((_a = viewer.isDestroyed) === null || _a === void 0 ? void 0 : _a.call(viewer)))
        return baseWidth;
    const alt = (_c = (_b = viewer.camera.positionCartographic) === null || _b === void 0 ? void 0 : _b.height) !== null && _c !== void 0 ? _c : 0;
    if (alt <= LINE_WIDTH_NEAR_ALT)
        return baseWidth;
    if (alt >= LINE_WIDTH_FAR_ALT)
        return baseWidth * LINE_WIDTH_FAR_SCALE;
    const t = (alt - LINE_WIDTH_NEAR_ALT) / (LINE_WIDTH_FAR_ALT - LINE_WIDTH_NEAR_ALT);
    return baseWidth * (1 - t * (1 - LINE_WIDTH_FAR_SCALE));
}, false);
// Comment avatar pin: a speech-bubble teardrop with the author's initial
// inside. Tail tip sits at the world anchor (verticalOrigin: BOTTOM places
// the bottom of the canvas at the world point, and the tail tip is drawn
// near the canvas bottom). Optional ringColor strokes the bubble outline
// to signal a tag.
export function createCommentAvatarPin(dataSource, position, initial, fillColor, fgColor, ringColor, opacity = 1.0, viewer) {
    const badgeCanvas = getCommentAvatarCanvas(initial, fillColor, fgColor, ringColor, opacity);
    // Far-zoom dot uses the user color so identity stays legible at altitude.
    const dotCanvas = getPlacemarkFarDotCanvas(fillColor, opacity);
    const imageProperty = viewer
        ? new Cesium.CallbackProperty(() => {
            var _a, _b, _c;
            if (!viewer || ((_a = viewer.isDestroyed) === null || _a === void 0 ? void 0 : _a.call(viewer)))
                return badgeCanvas;
            const alt = (_c = (_b = viewer.camera.positionCartographic) === null || _b === void 0 ? void 0 : _b.height) !== null && _c !== void 0 ? _c : 0;
            return alt > PLACEMARK_BADGE_TO_DOT_ALTITUDE ? dotCanvas : badgeCanvas;
        }, false)
        : badgeCanvas;
    return dataSource.entities.add({
        position,
        billboard: {
            image: imageProperty,
            verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            scale: 1.0,
            scaleByDistance: PLACEMARK_SCALE_BY_DISTANCE,
        },
    });
}
// Comment pin: same billboard shape as a placemark but uses the
// comment-specific badge canvas (no glyph, optional stroke color tied to
// the comment's first tag). Far-zoom dot reuses the placemark's dot canvas
// so cluster + zoom-out behavior is consistent.
export function createCommentPin(dataSource, position, fillColor, strokeColor, opacity = 1.0, viewer) {
    const badgeCanvas = getCommentBadgeCanvas(fillColor, strokeColor, opacity);
    const dotCanvas = getPlacemarkFarDotCanvas(strokeColor !== null && strokeColor !== void 0 ? strokeColor : fillColor, opacity);
    const imageProperty = viewer
        ? new Cesium.CallbackProperty(() => {
            var _a, _b, _c;
            if (!viewer || ((_a = viewer.isDestroyed) === null || _a === void 0 ? void 0 : _a.call(viewer)))
                return badgeCanvas;
            const alt = (_c = (_b = viewer.camera.positionCartographic) === null || _b === void 0 ? void 0 : _b.height) !== null && _c !== void 0 ? _c : 0;
            return alt > PLACEMARK_BADGE_TO_DOT_ALTITUDE ? dotCanvas : badgeCanvas;
        }, false)
        : badgeCanvas;
    return dataSource.entities.add({
        position,
        billboard: {
            image: imageProperty,
            verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            scale: 1.0,
            scaleByDistance: PLACEMARK_SCALE_BY_DISTANCE,
        },
    });
}
export function createPlacemarkPin(dataSource, position, color, iconName, opacity = 1.0, viewer, scale = 1.0) {
    const badgeCanvas = getPlacemarkIconCanvas(iconName, color, opacity);
    const dotCanvas = getPlacemarkFarDotCanvas(color, opacity);
    const imageProperty = viewer
        ? new Cesium.CallbackProperty(() => {
            var _a, _b, _c;
            if (!viewer || ((_a = viewer.isDestroyed) === null || _a === void 0 ? void 0 : _a.call(viewer)))
                return badgeCanvas;
            const alt = (_c = (_b = viewer.camera.positionCartographic) === null || _b === void 0 ? void 0 : _b.height) !== null && _c !== void 0 ? _c : 0;
            return alt > PLACEMARK_BADGE_TO_DOT_ALTITUDE ? dotCanvas : badgeCanvas;
        }, false)
        : badgeCanvas;
    return dataSource.entities.add({
        position,
        billboard: {
            image: imageProperty,
            verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            scale,
            scaleByDistance: PLACEMARK_SCALE_BY_DISTANCE,
        }
    });
}
// Position anchor for the Label tool. The visible text is drawn by a
// separate DOM overlay layer (LabelMapOverlay) which also catches all
// pointer events directly on the rendered text — the entity itself no
// longer needs to be pickable. We keep an entity with no graphics so
// useLabels can still hold a Cesium reference for position updates and
// dataSource membership.
export function createLabelPickerEntity(dataSource, position) {
    return dataSource.entities.add({
        position,
    });
}
export function createPlacemarkLabel(dataSource, position, name, opacity = 1.0, scale = 1.0) {
    return dataSource.entities.add({
        position,
        label: {
            text: name,
            font: ENTITY_STYLES.label.font,
            fillColor: Cesium.Color.WHITE.withAlpha(opacity),
            outlineColor: Cesium.Color.BLACK.withAlpha(opacity),
            outlineWidth: ENTITY_STYLES.label.outlineWidth,
            style: Cesium.LabelStyle.FILL_AND_OUTLINE,
            verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
            pixelOffset: new Cesium.Cartesian2(0, placemarkLabelOffsetY(scale)),
            scaleByDistance: PLACEMARK_SCALE_BY_DISTANCE,
            pixelOffsetScaleByDistance: PLACEMARK_SCALE_BY_DISTANCE,
            distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, PLACEMARK_BADGE_TO_DOT_ALTITUDE),
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY
        }
    });
}
export function createPathPoint(dataSource, position, color, pointType, opacity = 1.0) {
    const baseColor = Cesium.Color.fromCssColorString(color);
    let pointColor;
    let outlineColor;
    if (pointType === 'first') {
        pointColor = baseColor.withAlpha(opacity);
        outlineColor = Cesium.Color.BLACK.withAlpha(opacity);
    }
    else if (pointType === 'last') {
        pointColor = Cesium.Color.BLACK.withAlpha(opacity);
        outlineColor = baseColor.withAlpha(opacity);
    }
    else {
        pointColor = baseColor.withAlpha(opacity * 0.5);
        outlineColor = baseColor.withAlpha(opacity);
    }
    return dataSource.entities.add({
        position,
        point: {
            pixelSize: ENTITY_STYLES.point.pixelSize,
            color: pointColor,
            outlineColor,
            outlineWidth: ENTITY_STYLES.point.outlineWidth,
            scaleByDistance: VERTEX_SCALE_BY_DISTANCE,
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY
        }
    });
}
export function createPathLine(dataSource, positions, color, opacity = 1.0, width = ENTITY_STYLES.line.width, viewer) {
    return dataSource.entities.add({
        polyline: {
            positions,
            width: viewer ? makePolylineWidthProperty(width, viewer) : width,
            material: new Cesium.ColorMaterialProperty(Cesium.Color.fromCssColorString(color).withAlpha(opacity)),
            clampToGround: true
        }
    });
}
export function createPathLabel(dataSource, midpoint, text, opacity = 1.0) {
    return dataSource.entities.add({
        position: midpoint,
        label: {
            text,
            font: ENTITY_STYLES.segmentLabel.font,
            fillColor: Cesium.Color.WHITE.withAlpha(opacity),
            outlineColor: Cesium.Color.BLACK.withAlpha(opacity),
            outlineWidth: ENTITY_STYLES.segmentLabel.outlineWidth,
            style: Cesium.LabelStyle.FILL_AND_OUTLINE,
            verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
            pixelOffset: new Cesium.Cartesian2(0, -10),
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY
        }
    });
}
export function createPolygonVertex(dataSource, position, color, isFirst, opacity = 1.0) {
    const baseColor = Cesium.Color.fromCssColorString(color);
    return dataSource.entities.add({
        position,
        point: {
            pixelSize: ENTITY_STYLES.polygonVertex.pixelSize,
            color: isFirst
                ? baseColor.withAlpha(opacity)
                : baseColor.withAlpha(opacity * 0.7),
            outlineColor: Cesium.Color.BLACK.withAlpha(opacity),
            outlineWidth: ENTITY_STYLES.polygonVertex.outlineWidth,
            scaleByDistance: VERTEX_SCALE_BY_DISTANCE,
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY
        }
    });
}
export function createPolygonOutline(dataSource, positions, color, closed, opacity = 1.0, width = ENTITY_STYLES.line.width, viewer) {
    const outlinePositions = closed ? [...positions, positions[0]] : positions;
    return dataSource.entities.add({
        polyline: {
            positions: outlinePositions,
            width: viewer ? makePolylineWidthProperty(width, viewer) : width,
            material: new Cesium.ColorMaterialProperty(Cesium.Color.fromCssColorString(color).withAlpha(opacity)),
            clampToGround: true
        }
    });
}
export function createPolygonFill(dataSource, positions, color, opacity = 1.0) {
    return dataSource.entities.add({
        polygon: {
            hierarchy: new Cesium.PolygonHierarchy(positions),
            material: Cesium.Color.fromCssColorString(color).withAlpha(opacity),
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND
        }
    });
}
export function createPolygonLabel(dataSource, positions, text, opacity = 1.0) {
    const center = Cesium.BoundingSphere.fromPoints(positions).center;
    return dataSource.entities.add({
        position: center,
        label: {
            text,
            font: ENTITY_STYLES.label.font,
            fillColor: Cesium.Color.WHITE.withAlpha(opacity),
            outlineColor: Cesium.Color.BLACK.withAlpha(opacity),
            outlineWidth: ENTITY_STYLES.label.outlineWidth,
            style: Cesium.LabelStyle.FILL_AND_OUTLINE,
            verticalOrigin: Cesium.VerticalOrigin.CENTER,
            horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY
        }
    });
}
export function createMeasurementPoint(dataSource, position, color, isStart, opacity = 1.0) {
    const baseColor = Cesium.Color.fromCssColorString(color);
    if (isStart) {
        return dataSource.entities.add({
            position,
            point: {
                pixelSize: ENTITY_STYLES.point.pixelSize,
                color: baseColor.withAlpha(opacity),
                outlineColor: Cesium.Color.BLACK.withAlpha(opacity),
                outlineWidth: ENTITY_STYLES.point.outlineWidth,
                heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
                disableDepthTestDistance: Number.POSITIVE_INFINITY
            }
        });
    }
    return dataSource.entities.add({
        position,
        point: {
            pixelSize: ENTITY_STYLES.point.pixelSize,
            color: Cesium.Color.BLACK.withAlpha(opacity),
            outlineColor: baseColor.withAlpha(opacity),
            outlineWidth: ENTITY_STYLES.measurementEndOutline,
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY
        }
    });
}
export function createMeasurementLine(dataSource, positions, color, opacity = 1.0, width = ENTITY_STYLES.line.width, viewer) {
    return dataSource.entities.add({
        polyline: {
            positions,
            width: viewer ? makePolylineWidthProperty(width, viewer) : width,
            material: new Cesium.ColorMaterialProperty(Cesium.Color.fromCssColorString(color).withAlpha(opacity)),
            clampToGround: true
        }
    });
}
export function createMeasurementLabel(dataSource, midpoint, text, opacity = 1.0) {
    return dataSource.entities.add({
        position: midpoint,
        label: {
            text,
            font: ENTITY_STYLES.label.font,
            fillColor: Cesium.Color.WHITE.withAlpha(opacity),
            outlineColor: Cesium.Color.BLACK.withAlpha(opacity),
            outlineWidth: ENTITY_STYLES.label.outlineWidth,
            style: Cesium.LabelStyle.FILL_AND_OUTLINE,
            verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
            pixelOffset: new Cesium.Cartesian2(0, -15),
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY
        }
    });
}
export function createGlowHighlight(dataSource, positions, color) {
    const glowColor = Cesium.Color.fromCssColorString(color).withAlpha(ENTITY_STYLES.highlight.alpha);
    return dataSource.entities.add({
        polyline: {
            positions,
            width: ENTITY_STYLES.highlight.width,
            material: new Cesium.ColorMaterialProperty(glowColor),
            clampToGround: true
        }
    });
}
export function updatePointOpacity(entity, color, opacity, variant) {
    var _a;
    if (!entity.point)
        return;
    const baseColor = Cesium.Color.fromCssColorString(color);
    switch (variant) {
        case 'first':
        case 'start':
            entity.point.color = new Cesium.ConstantProperty(baseColor.withAlpha(opacity));
            entity.point.outlineColor = new Cesium.ConstantProperty(Cesium.Color.BLACK.withAlpha(opacity));
            break;
        case 'last':
        case 'end':
            entity.point.color = new Cesium.ConstantProperty(Cesium.Color.BLACK.withAlpha(opacity));
            entity.point.outlineColor = new Cesium.ConstantProperty(baseColor.withAlpha(opacity));
            break;
        case 'middle':
            entity.point.color = new Cesium.ConstantProperty(baseColor.withAlpha(opacity * 0.5));
            entity.point.outlineColor = new Cesium.ConstantProperty(baseColor.withAlpha(opacity));
            break;
        case 'polygonFirst':
            entity.point.color = new Cesium.ConstantProperty(baseColor.withAlpha(opacity));
            entity.point.outlineColor = new Cesium.ConstantProperty(Cesium.Color.BLACK.withAlpha(opacity));
            break;
        case 'polygonOther':
            entity.point.color = new Cesium.ConstantProperty(baseColor.withAlpha(opacity * 0.7));
            entity.point.outlineColor = new Cesium.ConstantProperty(Cesium.Color.BLACK.withAlpha(opacity));
            break;
        default: {
            const currentColor = (_a = entity.point.color) === null || _a === void 0 ? void 0 : _a.getValue(Cesium.JulianDate.now());
            if (currentColor) {
                entity.point.color = new Cesium.ConstantProperty(currentColor.withAlpha(opacity));
            }
            break;
        }
    }
}
export function updateLineOpacity(entity, color, opacity) {
    if (!entity.polyline)
        return;
    entity.polyline.material = new Cesium.ColorMaterialProperty(Cesium.Color.fromCssColorString(color).withAlpha(opacity));
}
// A sketch stroke's own opacity, kept off the entity itself so nothing has to
// clean it up: the entry goes when the entity does.
const lineBaseOpacity = new WeakMap();
export function registerLineBaseOpacity(entity, opacity) {
    lineBaseOpacity.set(entity, opacity);
}
// A sketch draws each stroke in its own colour and its own opacity, so
// artifact-level opacity multiplies the registered base rather than replacing
// it. Reading the entity's current alpha instead would compound across slider
// drags once the bases differ.
export function updateLineAlpha(entity, opacity) {
    var _a, _b;
    if (!entity.polyline)
        return;
    const material = entity.polyline.material;
    const currentColor = (_a = material === null || material === void 0 ? void 0 : material.color) === null || _a === void 0 ? void 0 : _a.getValue(Cesium.JulianDate.now());
    if (!currentColor)
        return;
    const base = (_b = lineBaseOpacity.get(entity)) !== null && _b !== void 0 ? _b : 1;
    entity.polyline.material = new Cesium.ColorMaterialProperty(currentColor.withAlpha(base * opacity));
}
export function updateLabelOpacity(entity, opacity) {
    if (!entity.label)
        return;
    entity.label.fillColor = new Cesium.ConstantProperty(Cesium.Color.WHITE.withAlpha(opacity));
    entity.label.outlineColor = new Cesium.ConstantProperty(Cesium.Color.BLACK.withAlpha(opacity));
}
export function updateBillboardOpacity(entity, color, opacity, iconName, viewer) {
    if (!entity.billboard)
        return;
    const badgeCanvas = getPlacemarkIconCanvas(iconName, color, opacity);
    const dotCanvas = getPlacemarkFarDotCanvas(color, opacity);
    if (viewer) {
        entity.billboard.image = new Cesium.CallbackProperty(() => {
            var _a, _b, _c;
            if (!viewer || ((_a = viewer.isDestroyed) === null || _a === void 0 ? void 0 : _a.call(viewer)))
                return badgeCanvas;
            const alt = (_c = (_b = viewer.camera.positionCartographic) === null || _b === void 0 ? void 0 : _b.height) !== null && _c !== void 0 ? _c : 0;
            return alt > PLACEMARK_BADGE_TO_DOT_ALTITUDE ? dotCanvas : badgeCanvas;
        }, false);
    }
    else {
        entity.billboard.image = new Cesium.ConstantProperty(badgeCanvas);
    }
}
export function updatePolygonFillOpacity(entity, color, opacity, baseFillAlpha) {
    if (!entity.polygon)
        return;
    entity.polygon.material = new Cesium.ColorMaterialProperty(Cesium.Color.fromCssColorString(color).withAlpha(opacity * baseFillAlpha));
}
